import React, { useState, useRef } from "react";
import {
  FaBriefcase,
  FaCalendar,
  FaMapMarkerAlt,
  FaExternalLinkAlt,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";

const experiences = [
  {
    company: "Roaspy",
    location: "US",
    position: "Backend Engineer",
    period: "November, 2025 - Present",
    website: "https://www.roaspy.com",
    summary:
      "Building comprehensive ads analytics platform with real-time dashboards. Optimizing database queries and implementing indexing strategies for faster performance. Integrated Google Ads API for real-time ad tracking and developed RESTful APIs for analytics data access.",
    technologies: [
      "Fastify",
      "PostgreSQL",
      "TypeScript",
      "Google Ads API",
      "Redis",
      "Node.js",
    ],
  },
  {
    company: "Xdezo Limited",
    location: "London, Greater London",
    position: "Product Engineer",
    period: "Jan, 2025 - Present",
    website: "https://xdezo.co.uk",
    summary:
      "Leading engineering team to deliver MVPs using modern full-stack technologies. Architected scalable backend systems and implemented CI/CD pipelines with Docker for automated deployments. Managing full software development lifecycle while collaborating with cross-functional teams.",
    technologies: [
      "Next.js",
      "React",
      "NestJS",
      "PostgreSQL",
      "AWS",
      "MySql",
      "React Query",
      "TypeOrm",
      "Redis",
      "Bull MQ",
      "Docker",
    ],
  },
  {
    company: "Freelancer",
    location: "Kathmandu",
    position: "Full Stack Engineer",
    period: "DEC, 2023 - Present",
    summary:
      "Built multiple full-stack applications including coupon store, Learning Management System, and matchmaking platforms. Designed scalable PostgreSQL schemas, deployed on Azure/AWS, and developed NestJS backend APIs. Optimized WebSocket architecture for real-time features and scaled systems to support thousands of concurrent users.",
    technologies: [
      "Next.js",
      "React",
      "NestJS",
      "PostgreSQL",
      "MongoDB",
      "AWS",
      "Azure",
      "Jest",
      "WebSocket",
      "Socket.IO",
      "TypeScript",
    ],
  },
  {
    company: "Saral-Lagani",
    location: "Kathmandu",
    position: "MERN Dev",
    period: "Nov, 2023 - Apr, 2024",
    website: "https://www.sarallagani.com/",
    summary:
      "Developed stock management system features and broker analytics website with Next.js. Created cross-platform mobile app with React Native and migrated 70% of codebase to Next.js 14. Built promo-code influencer tracking system with Express.js APIs.",
    technologies: ["Next.js", "React", "React Native", "Express.js", "SQL"],
  },
  {
    company: "Webx-Nepal",
    location: "Kathmandu",
    position: "Backend Intern",
    period: "Aug, 2023 - Nov, 2023",
    website: "https://webxnep.com/",
    summary:
      "Learned industry best practices while developing Express.js servers and APIs. Deployed applications using Docker on VPS and mastered MongoDB Aggregation pipelines. Implemented data validation with Zod and optimized performance using Redis caching.",
    technologies: [
      "Express.js",
      "TypeScript",
      "Redis",
      "MongoDB",
      "Zod",
      "Docker",
    ],
  },
];

const Experience = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const scrollToSlide = (index: number) => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const card = container.children[index] as HTMLElement;
      if (card) {
        const gap = 24; // gap-6 = 1.5rem = 24px
        const scrollPosition = card.offsetLeft - gap;
        container.scrollTo({ left: scrollPosition, behavior: "smooth" });
        setCurrentIndex(index);
      }
    }
  };

  const nextSlide = () => {
    const next = (currentIndex + 1) % experiences.length;
    scrollToSlide(next);
  };

  const prevSlide = () => {
    const prev = (currentIndex - 1 + experiences.length) % experiences.length;
    scrollToSlide(prev);
  };

  // Update current index on scroll
  React.useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const cardWidth = container.children[0]?.clientWidth || 0;
      const gap = 24;
      const scrollPos = container.scrollLeft;
      const newIndex = Math.round(scrollPos / (cardWidth + gap));
      if (
        newIndex !== currentIndex &&
        newIndex >= 0 &&
        newIndex < experiences.length
      ) {
        setCurrentIndex(newIndex);
      }
    };

    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, [currentIndex]);

  return (
    <section
      id="experience"
      className="relative py-20 bg-[#0a0a0f] border-t border-white/5"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div
          className="mb-12 flex items-center justify-between"
          data-aos="fade-up"
        >
          <div>
            <h2 className="text-4xl sm:text-5xl md:text-6xl font-light text-white mb-3">
              Experience
            </h2>
            <p className="text-gray-400 text-lg">Professional journey</p>
          </div>

          {/* Navigation buttons */}
          <div className="flex gap-2">
            <button
              onClick={prevSlide}
              className="p-3 rounded-lg border border-white/10 text-gray-400 hover:text-white hover:border-white/20 transition-all duration-300"
              aria-label="Previous"
            >
              <FaChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={nextSlide}
              className="p-3 rounded-lg border border-white/10 text-gray-400 hover:text-white hover:border-white/20 transition-all duration-300"
              aria-label="Next"
            >
              <FaChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Carousel container */}
        <div
          ref={scrollContainerRef}
          className="flex gap-6 overflow-x-auto scrollbar-hide snap-x snap-mandatory scroll-smooth pb-4"
          style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
        >
          {experiences.map((exp, index) => (
            <div
              key={index}
              className="flex-shrink-0 w-full md:w-[calc(50%-12px)] snap-start"
            >
              <div className="bg-white/5 border border-white/10 rounded-lg p-6 md:p-8 hover:border-white/20 hover:bg-white/10 transition-all duration-300 h-full">
                <div className="flex flex-col md:flex-row md:items-start md:justify-between mb-6 gap-4">
                  <div className="flex-1">
                    <h3 className="text-2xl md:text-3xl font-medium text-white mb-2 group-hover:text-blue-400 transition-colors duration-300">
                      {exp.position}
                    </h3>
                    <div className="flex items-center text-gray-400 mb-4">
                      <FaBriefcase className="mr-2 text-sm" />
                      <span className="text-base">{exp.company}</span>
                      {exp.website && (
                        <a
                          href={exp.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="ml-2 p-1 rounded border border-white/10 text-gray-400 hover:text-white hover:border-white/20 transition-all duration-300"
                        >
                          <FaExternalLinkAlt className="w-3 h-3" />
                        </a>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col gap-2 text-sm text-gray-400 md:text-right">
                    <div className="flex items-center md:justify-end">
                      <FaCalendar className="mr-2" />
                      <span>{exp.period}</span>
                    </div>
                    <div className="flex items-center md:justify-end">
                      <FaMapMarkerAlt className="mr-2" />
                      <span>{exp.location}</span>
                    </div>
                  </div>
                </div>

                <div className="mb-6">
                  <div className="flex flex-wrap gap-2">
                    {exp.technologies.map((tech, i) => (
                      <span
                        key={i}
                        className="px-3 py-1 bg-white/5 border border-white/10 rounded text-xs text-gray-400"
                      >
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>

                <p className="text-gray-400 text-base leading-relaxed">
                  {exp.summary}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Dots indicator */}
        <div className="flex justify-center gap-2 mt-8">
          {experiences.map((_, index) => (
            <button
              key={index}
              onClick={() => scrollToSlide(index)}
              className={`w-2 h-2 rounded-full transition-all duration-300 ${currentIndex === index
                  ? "bg-white w-8"
                  : "bg-white/20 hover:bg-white/40"
                }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Experience;
